import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, SectionHead } from "@/components/Reveal";
import { CtaBand } from "@/pages/Home";
import { useLanguage } from "@/context/LanguageContext";

const SKYLINE = "https://images.unsplash.com/photo-1506966953602-c20cc11f75e3?auto=format&fit=crop&w=1800&q=80";

export const PageHero = ({ overline, title, subtitle, testid }) => (
  <section data-testid={testid} className="bg-ink grain-overlay relative pt-40 pb-20 sm:pt-48 sm:pb-24">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold flex items-center gap-3">
          <span className="h-px w-10 bg-gold inline-block" /> {overline}
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h1 className="mt-5 font-serif text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight max-w-3xl text-balance">
          {title}
        </h1>
      </Reveal>
      {subtitle && (
        <Reveal delay={0.2}>
          <p className="mt-6 text-cream/60 text-base sm:text-lg max-w-2xl leading-relaxed">{subtitle}</p>
        </Reveal>
      )}
    </div>
  </section>
);

export default function About() {
  const { t } = useLanguage();
  return (
    <main data-testid="about-page">
      <Seo
        title="About Carlos Vindel — Miami Real Estate Agent"
        siteName="Carlos Vindel Realty"
        description="Meet Carlos Vindel: a driven, hands-on South Florida real estate agent specializing in first-time buyers and residential moves across Miami."
      />
      <PageHero testid="about-hero" overline={t.about.overline} title={t.about.title} subtitle={t.about.lead} />

      <section className="bg-cream py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-14 items-start">
          <Reveal className="lg:col-span-2 relative">
            <div className="absolute -top-4 -left-4 w-full h-full border border-gold/50 pointer-events-none" />
            <img
              src="/images/carlos-headshot.jpg"
              alt="Carlos Vindel, Miami real estate agent"
              loading="lazy"
              className="relative w-full aspect-[4/5] object-cover object-top"
            />
          </Reveal>
          <div className="lg:col-span-3">
            <Reveal>
              <p className="text-[#525D6B] leading-relaxed text-lg">{t.about.bio1}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-[#525D6B] leading-relaxed">{t.about.bio2}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-10 relative overflow-hidden">
                <img src={SKYLINE} alt="Miami skyline" loading="lazy" className="w-full aspect-[21/9] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/40 to-transparent flex items-center">
                  <div className="p-7 max-w-md">
                    <p className="text-gold text-[10px] uppercase tracking-[0.3em] font-semibold">{t.about.personalTitle}</p>
                    <p className="mt-3 font-serif italic text-cream text-lg leading-relaxed">{t.about.personal}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="why-carlos-chapters" className="bg-white py-24 sm:py-32 border-y border-sand/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-16">
            <SectionHead overline={t.about.chaptersOverline} title={t.about.chaptersTitle} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-14">
            {t.about.chapters.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08} className="group border-t border-sand pt-8">
                <span className="font-serif text-5xl text-gold/40 group-hover:text-gold transition-colors duration-500">{c.num}</span>
                <h3 className="mt-4 font-serif text-2xl text-ink">{c.title}</h3>
                <p className="mt-3 text-[#525D6B] leading-relaxed">{c.desc}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-16">
            <Link
              to="/contact"
              data-testid="about-cta-link"
              className="group inline-flex items-center gap-3 bg-ink hover:bg-charcoal text-cream font-semibold text-sm tracking-wide px-8 py-4 rounded-full transition-colors duration-300"
            >
              {t.about.cta}
              <ArrowRight className="w-4 h-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
